/**
 * Live talker list for the in-channel screen.
 *
 * Renders one row per current floor holder, keyed by `connection_id`. The
 * protocol requires clients to treat the talker list as a *set* (per
 * `PROTOCOL.md` § Handshake) so adds and removes are idempotent —
 * receiving `floor_taken` for someone already shown, or `floor_released`
 * for someone not shown, is a no-op.
 *
 * No virtual DOM — the list rarely has more than `K=3` entries, so a
 * direct DOM map is the simplest correct implementation.
 */

import type { TalkerInfo } from "../protocol";

/** Constructor options. */
export interface TalkerListOptions {
    /** Container the list is appended into. */
    container: HTMLElement;
    /** Initial set of talkers, e.g. from `hello_ok.current_talkers`. */
    initial?: TalkerInfo[];
}

/** Handle for mutating the list. */
export interface TalkerListHandle {
    /** Mark `talker` as currently holding the floor. Idempotent. */
    add(talker: TalkerInfo): void;
    /** Remove the entry for `connection_id`. Idempotent. */
    remove(connectionId: string): void;
    /** Replace the whole set in one call (e.g. on reconnect). */
    setAll(talkers: TalkerInfo[]): void;
    /** Detach all DOM nodes belonging to the list. */
    destroy(): void;
}

/**
 * Construct the talker-list view inside `container`. Returns a handle
 * for incremental updates as `floor_taken` / `floor_released` events
 * arrive on the transport.
 */
export function createTalkerList(opts: TalkerListOptions): TalkerListHandle {
    const root = document.createElement("div");
    root.className = "talker-list";
    opts.container.appendChild(root);

    /** connection_id → row element */
    const rows = new Map<string, HTMLElement>();

    const addInternal = (t: TalkerInfo): void => {
        if (rows.has(t.connection_id)) return;
        const row = document.createElement("div");
        row.className = "talker";
        row.dataset.connectionId = t.connection_id;

        const dot = document.createElement("span");
        dot.className = "dot";
        dot.setAttribute("aria-hidden", "true");

        const name = document.createElement("span");
        name.className = "name";
        name.textContent = t.display_name;

        row.appendChild(dot);
        row.appendChild(name);
        root.appendChild(row);
        rows.set(t.connection_id, row);
    };

    const removeInternal = (connectionId: string): void => {
        const row = rows.get(connectionId);
        if (row === undefined) return;
        row.remove();
        rows.delete(connectionId);
    };

    for (const t of opts.initial ?? []) addInternal(t);

    return {
        add: addInternal,
        remove: removeInternal,
        setAll(talkers: TalkerInfo[]): void {
            const wanted = new Set(talkers.map((t) => t.connection_id));
            // Remove any rows no longer present.
            for (const id of Array.from(rows.keys())) {
                if (!wanted.has(id)) removeInternal(id);
            }
            // Add any new rows.
            for (const t of talkers) addInternal(t);
        },
        destroy(): void {
            root.remove();
            rows.clear();
        },
    };
}
